import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { apiClient } from '@/lib/apiClient'
import { API_ROUTES, PAGINATION } from '@/constants/apiRoutes'
import { AffiliationManagementList, CreateAffiliationParam, CreatedAffiliationResponse, EditAffiliationParam } from '@/types/affiliationManagement'

import { AffiliationManagementSearchParams } from '@/types/affiliationManagement';

export const getAffiliationList = createAsyncThunk<AffiliationManagementList, AffiliationManagementSearchParams>(
    `${API_ROUTES.ADMIN_AFFILIATION_LIST_ENDPOINT}`,
    async ({
        affiliations_id,
        affiliations_id_search_type,
        affiliations_name,
        page,
        page_size,
        affiliations_name_search_type,
        note,
        note_search_type,
        display_order_min,
        display_order_max,
    }) => {
        const rawParams = {
            affiliations_id: affiliations_id,
            affiliations_id_search_type: affiliations_id_search_type,
            affiliations_name: affiliations_name,
            affiliations_name_search_type: affiliations_name_search_type,
            note: note,
            note_search_type: note_search_type,
            display_order_min: display_order_min,
            display_order_max: display_order_max,
            page: page,
            page_size: page_size,
        };

        const params = new URLSearchParams();
        Object.entries(rawParams).forEach(([key, value]) => {
            if (value !== undefined && value !== '') {
                params.append(key, String(value));
            }
        });

        const res = await apiClient.get<AffiliationManagementList>(
            `${API_ROUTES.ADMIN_AFFILIATION_LIST_ENDPOINT}?${params.toString()}`
        );
        return res;
    }
);

export const createAffiliation = createAsyncThunk<CreatedAffiliationResponse, CreateAffiliationParam>(API_ROUTES.ADMIN_AFFILIATION_ENDPOINT, async (createAffiliationParam) => {
    const res = await apiClient.post<CreatedAffiliationResponse>(API_ROUTES.ADMIN_AFFILIATION_ENDPOINT, { ...createAffiliationParam });
    return res;
});

export const editAffiliation = createAsyncThunk<CreatedAffiliationResponse, EditAffiliationParam>(API_ROUTES.ADMIN_AFFILIATION_ENDPOINT, async (editAffiliationParam) => {
    const { affiliation_id, ...rest } = editAffiliationParam;
    const res = await apiClient.put<CreatedAffiliationResponse>(`${API_ROUTES.ADMIN_AFFILIATION_ENDPOINT}/${affiliation_id}`, { ...rest });
    return res;
});

export const deleteAffiliation = createAsyncThunk<CreatedAffiliationResponse, { affiliation_id: string }>(API_ROUTES.ADMIN_AFFILIATION_ENDPOINT, async ({ affiliation_id }) => {
    const res = await apiClient.delete<CreatedAffiliationResponse>(`${API_ROUTES.ADMIN_AFFILIATION_ENDPOINT}/${affiliation_id}`);
    return res;
});

// Initial state
const initialState: AffiliationManagementList = {
    items: [],
    page: PAGINATION.PAGE,
    page_size: PAGINATION.PAGE_SIZE,
    total: 0,
    loading: false,
    error: null,
    status: '',
    message: '',
}

// Slice definition
const affiliationManagementSlice = createSlice({
    name: 'affiliationManagement',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(getAffiliationList.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.status = 'loading';
            })
            .addCase(getAffiliationList.fulfilled, (state, action) => {
                state.items = action.payload.items || [];
                state.page = action.payload.page || PAGINATION.PAGE;
                state.page_size = action.payload.page_size || PAGINATION.PAGE_SIZE;
                state.total = action.payload.total || 0;
                state.loading = false;
                state.status = 'success';
                state.message = action.payload.message || '';
            })
            .addCase(getAffiliationList.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string || action.error.message || 'Error';
                state.status = 'error';
                state.items = [];
            });
    },
});

export default affiliationManagementSlice.reducer;